/**
 * CountrySearchSelect — Filterable dropdown for ISO 3166 country list.
 *
 * A searchable dropdown for selecting a country from the ISO 3166-1 list.
 * Uses a portal-based Popover so dropdown content renders above table cells
 * and overflow containers (instead of clipping inside the row).
 *
 * Features:
 * - Search by country name or ISO code
 * - Shows top 30 countries by default
 * - Keyboard accessible (auto-focus search input)
 * - Portal rendering prevents z-index issues
 *
 * @see docs/country-dropdown-popover-plan.md — Step 2
 */

import { useState, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { ChevronDown } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Input } from '@/components/ui/input'
import { Text } from '@/components/ui/Text'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { COUNTRIES } from '@/lib/constants/demographics'
import { localizedCountryName } from '@/lib/utils/localized-country-name'
import type { CountrySearchSelectProps } from '@/types/components/audiences/mapping-components'

export function CountrySearchSelect({
  value,
  onChange,
  disabled,
  className,
  specialValueDisplay,
}: CountrySearchSelectProps) {
  const { t, i18n } = useTranslation('app')
  const [search, setSearch] = useState('')
  const [open, setOpen] = useState(false)

  const filtered = useMemo(() => {
    if (!search) return COUNTRIES.slice(0, 30) // Show top countries by default
    const q = search.toLowerCase()
    return COUNTRIES.filter((c) => {
      const localized = localizedCountryName(c.code, i18n.language)
      return (
        c.name.toLowerCase().includes(q) ||
        localized.toLowerCase().includes(q) ||
        c.code.toLowerCase().includes(q)
      )
    }).slice(0, 30)
  }, [search, i18n.language])

  const selectedCountry = COUNTRIES.find((c) => c.code === value)
  const specialSelected =
    specialValueDisplay && value === specialValueDisplay.wireValue

  const triggerLabel = (() => {
    if (specialSelected) {
      return specialValueDisplay.label
    }
    if (selectedCountry) {
      return `${localizedCountryName(selectedCountry.code, i18n.language)} (${selectedCountry.code})`
    }
    return t('audienceCreate.selectCountry')
  })()

  return (
    <Popover
      open={open}
      onOpenChange={(nextOpen) => {
        setOpen(nextOpen)
        if (!nextOpen) setSearch('')
      }}
    >
      <PopoverTrigger asChild>
        <button
          type="button"
          disabled={disabled}
          className={cn(
            'flex h-8 w-[200px] items-center justify-between rounded-md border border-input bg-background px-3 text-xs',
            'hover:bg-accent hover:text-accent-foreground',
            !value && !specialSelected && 'text-muted-foreground',
            disabled && 'cursor-not-allowed opacity-50',
            className,
          )}
        >
          <span className="truncate">{triggerLabel}</span>
          <ChevronDown className="h-3 w-3 shrink-0 opacity-50" />
        </button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-[280px] p-0">
        <div className="rounded-lg border border-border bg-popover shadow-md">
          <div className="p-2">
            <Input
              autoFocus
              placeholder={t('audienceCreate.searchCountry')}
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              className="h-7 text-xs"
            />
          </div>
          <div className="max-h-[200px] overflow-y-auto px-1 pb-1">
            {specialValueDisplay ? (
              <button
                type="button"
                onClick={() => {
                  onChange(specialValueDisplay.wireValue)
                  setOpen(false)
                  setSearch('')
                }}
                className={cn(
                  'mb-1 flex w-full items-center rounded px-2 py-1.5 text-left text-base hover:bg-accent',
                  specialSelected && 'bg-accent font-medium',
                )}
              >
                <Text as="span" size="xs" className="truncate text-base">
                  {specialValueDisplay.label}
                </Text>
              </button>
            ) : null}
            {filtered.length === 0 ? (
              <Text size="xs" color="muted" className="px-2 py-3 text-center block">
                {t('audienceCreate.noCountriesFound')}
              </Text>
            ) : (
              filtered.map((c) => (
                <button
                  key={c.code}
                  type="button"
                  onClick={() => {
                    onChange(c.code)
                    setOpen(false)
                    setSearch('')
                  }}
                  className={cn(
                    'flex w-full items-center rounded px-2 py-1.5 text-base hover:bg-accent',
                    c.code === value && 'bg-accent font-medium',
                  )}
                >
                  <Text as="span" size="xs" className="font-mono text-base text-muted-foreground w-8">{c.code}</Text>
                  <Text as="span" size="xs" className="ml-2 truncate text-base">
                    {localizedCountryName(c.code, i18n.language)}
                  </Text>
                </button>
              ))
            )}
          </div>
        </div>
      </PopoverContent>
    </Popover>
  )
}
